/**
 * Normalises FCM payloads into a shape the in-app toast can render.
 *
 * Background: the MyU notification backend wraps every push in a single
 * `data.message` field whose value is a JSON string like:
 *
 *   {
 *     "body": "Jitendra mentioned you in a comment",
 *     "type": "NewsCommentMention",
 *     "objectDataId": 19035228,
 *     "userIdToNotify": 6560765,
 *     "whoFiredUserBasicDetail": { id, name, surName, username, profileImage, … },
 *     "notificationCategory": "you",
 *     "boardName": "...",
 *     "messageId": "...",
 *     ...
 *   }
 *
 * FCM forces every `data` field to be a string, hence the JSON-in-a-string.
 * We unwrap it once here so the toast + click handler can work with a real
 * object instead of bespoke JSON.parse at every call-site.
 */

import type { IncomingPushMessage } from './types';
import { resolvePushDeepLink } from './pushDeepLink';

/** Sender block — matches the `whoFiredUserBasicDetail` shape. */
export interface PushActor {
  id?: number;
  name?: string;
  surName?: string;
  username?: string;
  profileImage?: string;
  profileImageThumbnail?: string;
}

/** The decoded payload inside `data.message`. All fields are optional. */
export interface PushMessagePayload {
  /** Server-classified type (`NewsCommentMention`, `Follow`, `JoinBoard`, …). */
  type?: string;
  title?: string | null;
  body?: string;
  url?: string | null;
  objectDataId?: number;
  userIdToNotify?: number;
  notificationCategory?: 'you' | 'following' | string;
  boardName?: string | null;
  boardId?: number;
  messageId?: string | null;
  whoFiredUserBasicDetail?: PushActor;
  /** Bag for anything we don't model explicitly. */
  [key: string]: unknown;
}

/** What the toast UI actually renders. */
export interface InAppNotification {
  /** Stable hint used for de-duping repeat pushes within a few seconds. */
  key: string;
  /** Bold first line — usually the sender's name or a generic title. */
  title: string;
  /** Secondary line — falls back to the FCM notification body. */
  body: string;
  /** Avatar URL (when the payload includes a sender). */
  avatarUrl?: string;
  /** Initials for the avatar fallback. */
  initials: string;
  /** In-app deep link to navigate to on click. */
  clickUrl: string;
  /** Decoded payload (kept around for downstream consumers). */
  payload: PushMessagePayload;
  /** Where the push entered the app. */
  source: IncomingPushMessage['source'];
  /** Original receive time. */
  receivedAt: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initialsFrom(first?: string, last?: string): string {
  const a = (first ?? '').trim().charAt(0);
  const b = (last ?? '').trim().charAt(0);
  const out = `${a}${b}`.toUpperCase();
  return out || 'M';
}

/**
 * Click target for each known backend notificationType. Add new cases here
 * as the backend introduces more — the default is `/notifications`, which is
 * always a safe landing page.
 */
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Decode the JSON-stringified `data.message` field from an FCM payload.
 * Returns `null` when the field is missing or malformed — callers fall back
 * to the FCM `notification.title` / `notification.body` in that case.
 */
export function parsePushPayload(message: IncomingPushMessage): PushMessagePayload | null {
  const raw = message.data?.message;
  let parsed: PushMessagePayload = {};
  if (typeof raw === 'string' && raw.length > 0) {
    try {
      const value = JSON.parse(raw) as PushMessagePayload;
      if (value && typeof value === 'object') parsed = value;
    } catch {
      // Keep using the top-level FCM data fields below.
    }
  }

  // Some backend pushes (including FollowUserRequest) put the event type only
  // in the raw FCM data block instead of repeating it inside data.message.
  const topLevelType =
    message.data.type || message.data.notificationType || message.data.kind || undefined;
  const payload = { ...parsed, type: parsed.type || topLevelType };
  return Object.keys(payload).length > 0 ? payload : null;
}

/**
 * Build the UI-ready notification object from an incoming push.
 *
 * Resolution order for each field:
 *   - title:    actor name → payload.title → FCM notification.title → "MyU"
 *   - body:     payload.body → FCM notification.body → empty
 *   - clickUrl: explicit `url` (FCM or payload) → type-based mapping
 *   - avatar:   actor.profileImageThumbnail → profileImage → undefined
 */
export function buildInAppNotification(message: IncomingPushMessage): InAppNotification {
  const payload = parsePushPayload(message) ?? {};
  const actor = payload.whoFiredUserBasicDetail ?? {};
  const actorName = [actor.name, actor.surName].filter(Boolean).join(' ').trim();

  const title = actorName || payload.title || message.title || 'MyU';
  const body = payload.body || message.body || '';
  const avatarUrl = actor.profileImageThumbnail || actor.profileImage || undefined;
  const initials = initialsFrom(actor.name, actor.surName);
  const clickUrl = resolvePushDeepLink(payload, message.url);

  // De-dupe key: prefer the backend's messageId (stable across SDK + raw
  // delivery paths), fall back to the FCM tag, then to a synthesised value.
  const key =
    payload.messageId ||
    message.tag ||
    `${payload.type ?? 'push'}-${payload.objectDataId ?? ''}-${message.receivedAt}`;

  return {
    key,
    title,
    body,
    avatarUrl,
    initials,
    clickUrl,
    payload,
    source: message.source,
    receivedAt: message.receivedAt,
  };
}
